import { Card, CardContent, Stack, Typography, Box, Chip, alpha, useTheme } from "@mui/material"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import ContentCutIcon from "@mui/icons-material/ContentCut"
import dayjs from "dayjs"
import "dayjs/locale/hu"
import BookingDecisionButtons from "./BookingDecisionButtons"
import PersonAvatar from "./PersonAvatar"

const PendingBookingCard = ({ booking, onDecision }) => {
  const theme = useTheme()
  dayjs.locale("hu")

  return (
    <Card
      elevation={0}
      data-testid="pending-booking"
      data-booking-id={booking.id}
      sx={{
        width: "100%",
        borderRadius: "12px",
        overflow: "hidden",
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.25s ease, transform 0.25s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.13), 0 2px 8px rgba(0,0,0,0.06)",
        },
      }}
    >
      {/* Dark header band */}
      <Box
        sx={{
          bgcolor: "#1a1a1a",
          px: 2.5,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
            <PersonAvatar size={36} sx={{ flexShrink: 0 }} />
            <Stack minWidth={0}>
              <Typography
                fontWeight={700}
                fontSize={15}
                color="#fff"
                noWrap
              >
                {booking.name}
              </Typography>
              <Typography
                fontSize={11}
                color="rgba(255,255,255,0.45)"
                noWrap
              >
                {booking.phoneNumber}
              </Typography>
            </Stack>
          </Stack>

          <Chip
            label="Függőben"
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "10px",
              height: "22px",
              backgroundColor: alpha(theme.palette.warning.main, 0.18),
              color: theme.palette.warning.light,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
              flexShrink: 0,
              ml: 1,
            }}
          />
        </Stack>
      </Box>

      {/* White body */}
      <CardContent sx={{ p: 0 }}>
        {/* Info row */}
        <Stack
          direction="row"
          spacing={0}
          divider={<Box sx={{ width: "1px", bgcolor: "rgba(0,0,0,0.06)", my: 1.5 }} />}
          sx={{ px: 2.5, py: 2, borderBottom: "1px solid rgba(0,0,0,0.06)" }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flex={1} pr={2}>
            <CalendarTodayIcon sx={{ fontSize: 15, color: "text.secondary", flexShrink: 0 }} />
            <Stack spacing={0}>
              <Typography variant="body2" fontWeight={700} fontSize={12} color="text.primary">
                {booking.date.replaceAll("-", ".")}
              </Typography>
              <Typography variant="caption" textTransform="capitalize" color="text.secondary" fontSize={10}>
                {dayjs(booking.date).format("dddd")}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flex={1} pl={2}>
            <AccessTimeIcon sx={{ fontSize: 15, color: "text.secondary", flexShrink: 0 }} />
            <Typography variant="body2" fontWeight={700} fontSize={12} color="text.primary">
              {booking.startTime} – {booking.endTime}
            </Typography>
          </Stack>
        </Stack>

        {/* Supply name if present */}
        {booking.supply?.name && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              px: 2.5,
              py: 1.25,
              borderBottom: "1px solid rgba(0,0,0,0.06)",
              bgcolor: "rgba(0,0,0,0.015)",
            }}
          >
            <ContentCutIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography fontSize={12} color="text.secondary" fontStyle="italic">
              {booking.supply?.name}
            </Typography>
          </Stack>
        )}

        {/* Action buttons */}
        <Box sx={{ px: 2.5, py: 2 }}>
          <BookingDecisionButtons
            onApprove={() => onDecision("APPROVE", booking.id)}
            onReject={() => onDecision("REJECT", booking.id)}
          />
        </Box>
      </CardContent>
    </Card>
  )
}

export default PendingBookingCard